import { useRef } from 'react'
import type { RefObject } from 'react'
import type { DataSheetGridRef } from 'react-datasheet-grid'

// 시트 빈 행 정리 — 한 칸이라도 적힌 행만 남긴다 (디바이스·인터페이스·항목 시트 공통).
// - 표 아래 빈 행(pad-)은 적은 것이 없으면 버린다 (아래쪽 빈 행에만 적어도 그 위 빈 행이 같이 생기지 않게)
// - 이번 편집으로 비게 된 행은 지운다 (Delete·잘라내기로 다 지우면 그 행이 없어진다)
// - 우클릭 [삽입] 으로 막 만든 행, 이번에 건드리지 않은 행(다른 사람이 막 삽입한 행 등)은 둔다 — 커서가 떠날 때 비어 있으면 지운다

export const PAD = 'pad-'

export function dropBlankRows<R extends { key: string }>(rows: R[], before: R[], blank: (r: R) => boolean): R[] {
  const prev = new Map(before.map(r => [r.key, r]))
  return rows.filter(r => {
    if (!blank(r)) return true
    if (r.key.startsWith(PAD)) return false
    const old = prev.get(r.key)
    return !old || old === r
  })
}

/** 커서가 다른 행으로 옮겨 가면 떠난 행이 비어 있을 때 지운다 (우클릭 삽입만 하고 안 적은 행 등 — 잃는 것이 없어 알림 없이).
 *  onActiveCellChange 에서 부른다. 위 행이 빠지면 아래 행들이 한 칸 올라오므로 커서도 같이 올려 가려던 행에 둔다 */
export function useLeaveBlankRow<R extends { key: string }>(
  gridRef: RefObject<DataSheetGridRef | null>,
  rowsRef: RefObject<R[]>,
  isEmpty: (row: R) => boolean,
  remove: (key: string) => void,
) {
  const cursorRow = useRef<string | null>(null)
  return (cell: { col: number; row: number } | null) => {
    const rows = rowsRef.current
    const key = cell ? rows[cell.row]?.key : undefined
    const left = cursorRow.current
    if (key) cursorRow.current = key
    if (!cell || !key || !left || left === key || left.startsWith(PAD)) return
    const at = rows.findIndex(r => r.key === left)
    if (at < 0 || !isEmpty(rows[at])) return
    remove(left)
    if (cell.row > at) requestAnimationFrame(() => gridRef.current?.setActiveCell({ col: cell.col, row: cell.row - 1 }))
  }
}
